import googleTagManager from '@analytics/google-tag-manager'
import Analytics from 'analytics'
import Cookies from 'js-cookie'
import {
    LPS_ANALYTICS_SCRIPT,
    CONTAINER_ID,
    LORASTREAM_ANALYTIC_EVENTS,
    TRACKING_COOKIE_KEY,
} from '../configs/analytics'

// Create GA instance service using class for tracking events
class LoraStreamAnalytics {
    constructor(containerId = null) {
        if (!window.dataLayer) window.dataLayer = []
        this.containerId = containerId
        this.analytics = null
        this.analyticsPlugins = []
        this.orgId = this.getOrgIdFromScript()
        this.init()
    }

    init() {
        this.analyticsPlugins.push(
            googleTagManager(
                { containerId: CONTAINER_ID },
                {
                    name: 'google-tag-manager-lora',
                }
            )
        )
        this.containerId &&
            this.analyticsPlugins.push(
                Object.assign({}, googleTagManager({ containerId: this.containerId }), {
                    name: 'google-tag-manager-other',
                })
            )
        this.analytics = window._beliveAnalytics = Analytics({
            app: 'loraLiveStream',
            debug: true,
            plugins: this.analyticsPlugins,
        })
        this.checkFromSource()
        return this
    }

    trackEvent(event, data) {
        const clonedData = JSON.parse(JSON.stringify(data))
        const listItemWithDetail = []
        clonedData.items
            ?.filter((item) => item.details?.length > 0)
            ?.forEach((item) => {
                item.details.forEach((detail) => {
                    const itemDetail = JSON.parse(JSON.stringify({ ...item, ...detail }))
                    delete itemDetail.details
                    listItemWithDetail.push(itemDetail)
                })
            })
        if (listItemWithDetail.length > 0) {
            clonedData.items = clonedData.items.concat(listItemWithDetail).filter((item) => !item.details?.length)
        }
        window.dataLayer.push({ event, ...clonedData })
    }

    trackingWatchStream(data) {
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.WATCH_STREAM, data)
    }

    trackingShared(data) {
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.SHARE_STREAM, data)
    }

    trackingClickedOnProduct(data) {
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.PRODUCT_CLICK, data)
    }

    trackingViewItem(items = [], value = 0, currency = 'USD', data = {}) {
        this.checkFromSource()
        let fromSourcesRaw = Cookies.get(TRACKING_COOKIE_KEY.SV_CONTEXT) || '{}'
        let fromSources = JSON.parse(fromSourcesRaw)
        const payload = {
            items: items,
            currency: currency,
            value: value,
            show_id: fromSources?.shid,
            stream_id: fromSources?.strid,
            org_id: fromSources?.orgid,
            user_id: fromSources?.userid,
            raw_sources: JSON.stringify(items),
            video_id: fromSources?.vid,
            playlist_id: fromSources?.pid,
            product_id: items.length ? items[0].item_id : '',
            product_name: items.length ? items[0].item_name : '',
            product_price: items.length ? items[0].price : 0,
            product_quantity: items.length ? items[0].quantity : 0,
            ...data,
        }
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.LORA_VIEW_ITEM, payload)
    }

    trackingAddToCart(items = [], value = 0, currency = 'USD', data = {}) {
        this.checkFromSource()
        let fromSourcesRaw = Cookies.get(TRACKING_COOKIE_KEY.SV_CONTEXT) || '{}'
        let fromSources = JSON.parse(fromSourcesRaw)
        const payload = {
            items: items,
            currency: currency,
            value: value,
            show_id: fromSources?.shid,
            stream_id: fromSources?.strid,
            org_id: fromSources?.orgid,
            user_id: fromSources?.userid,
            raw_sources: JSON.stringify(items),
            video_id: fromSources?.vid,
            playlist_id: fromSources?.pid,
            product_id: items.length ? items[0].item_id : '',
            product_name: items.length ? items[0].item_name : '',
            product_price: value,
            product_quantity: items.length ? items[0].quantity : 0,
            ...data,
        }
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.ADD_TO_CART, payload)
        this.updateStoreItems(items, data)
    }

    trackingDecreaseFromCart(items = [], value = 0, currency = 'USD', data = {}) {
        this.checkFromSource()
        let fromSourcesRaw = Cookies.get(TRACKING_COOKIE_KEY.SV_CONTEXT) || '{}'
        let fromSources = JSON.parse(fromSourcesRaw)
        const payload = {
            items: items,
            currency: currency,
            value: value,
            show_id: fromSources?.shid,
            stream_id: fromSources?.strid,
            org_id: fromSources?.orgid,
            user_id: fromSources?.userid,
            raw_sources: JSON.stringify(items),
            video_id: fromSources?.vid,
            playlist_id: fromSources?.pid,
            product_id: items.length ? items[0].item_id : '',
            product_name: items.length ? items[0].item_name : '',
            product_price: value,
            product_quantity: items.length ? items[0].quantity : 0,
            ...data,
        }
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.REMOVE_FROM_CART, payload)
        this.updateStoreItems(items, data, { isDecrease: true })
    }

    trackingRemoveFromCart(items = [], value = 0, currency = 'USD', data = {}) {
        this.checkFromSource()
        let fromSourcesRaw = Cookies.get(TRACKING_COOKIE_KEY.SV_CONTEXT) || '{}'
        let fromSources = JSON.parse(fromSourcesRaw)
        const payload = {
            items: items,
            currency: currency,
            value: value,
            show_id: fromSources?.shid,
            stream_id: fromSources?.strid,
            org_id: fromSources?.orgid,
            user_id: fromSources?.userid,
            raw_sources: JSON.stringify(items),
            video_id: fromSources?.vid,
            playlist_id: fromSources?.pid,
            product_id: items.length ? items[0].item_id : '',
            product_name: items.length ? items[0].item_name : '',
            product_price: value,
            product_quantity: items.length ? items[0].quantity : 0,
            ...data,
        }
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.REMOVE_FROM_CART, payload)
        this.updateStoreItems(items, data, { isRemove: true })
    }

    trackingCheckout(items = [], value = 0, currency = 'USD', coupon = '', data) {
        items = this.syncWithTrackingItems(items)
        let fromSourcesRaw = Cookies.get(TRACKING_COOKIE_KEY.SV_CONTEXT) || '{}'
        let fromSources = JSON.parse(fromSourcesRaw)
        const payload = {
            items: items,
            currency: currency,
            coupon: coupon,
            value: value,
            show_id: fromSources?.shid,
            stream_id: fromSources?.strid,
            org_id: fromSources?.orgid || this.orgId,
            user_id: fromSources?.userid,
            raw_sources: JSON.stringify(items),
            video_id: fromSources?.vid,
            playlist_id: fromSources?.pid,
            ...data,
        }
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.PROCESS_CHECKOUT, payload)
    }

    trackingPurchaseSuccess(
        items = [],
        value = 0,
        currency = 'USD',
        transactionId = '',
        coupon = '',
        shipping = null,
        tax = null,
        data
    ) {
        items = this.syncWithTrackingItems(items)
        this.checkFromSource()
        let fromSourcesRaw = Cookies.get(TRACKING_COOKIE_KEY.SV_CONTEXT) || '{}'
        let fromSources = JSON.parse(fromSourcesRaw)
        const payload = {
            items,
            value,
            currency,
            coupon,
            shipping,
            tax,
            transaction_id: transactionId,
            show_id: fromSources?.shid,
            stream_id: fromSources?.strid,
            org_id: fromSources?.orgid || this.orgId,
            user_id: fromSources?.userid,
            video_id: fromSources?.vid,
            playlist_id: fromSources?.pid,
            raw_sources: JSON.stringify(items),
            ...data,
        }
        this.trackEvent(LORASTREAM_ANALYTIC_EVENTS.PURCHASE_SUCCESS, payload)
        Cookies.remove(TRACKING_COOKIE_KEY.SV_CONTEXT)
        Cookies.remove(TRACKING_COOKIE_KEY.SV_ITEMS)
    }

    updateStoreItemDetails(details, data, options) {
        const { isDecrease = false } = options

        let existItemIndex = null
        let newItem = null

        if (data.playlist_id || data.video_id) {
            existItemIndex = details.findIndex(
                (detail) =>
                    (!data.playlist_id || detail.playlist_id === data.playlist_id) && detail.video_id === data.video_id
            )
            if (existItemIndex === -1) {
                newItem = {
                    quantity: data.quantity,
                    video_id: data.video_id,
                    video_title: data.video_title,
                    playlist_id: data.playlist_id,
                    playlist_title: data.playlist_title,
                }
            }
        } else if (data.show_id || data.stream_id) {
            existItemIndex = details.findIndex(
                (detail) =>
                    detail.show_id === data.show_id &&
                    detail.stream_id === data.stream_id &&
                    detail.host_id === data.host_id
            )
            if (existItemIndex === -1) {
                newItem = {
                    ...data,
                    quantity: data.quantity,
                    show_id: data.show_id,
                    stream_id: data.stream_id,
                    host_id: data.host_id,
                }
            }
        }
        if (existItemIndex === -1) {
            if (!isDecrease) {
                details.push(newItem)
            } else if (details.length > 0) {
                details[0].quantity -= data.quantity
            }
        } else {
            if (isDecrease) details[existItemIndex].quantity -= data.quantity
            else details[existItemIndex].quantity += data.quantity
        }

        return details.filter((detail) => detail.quantity > 0)
    }

    updateStoreItems(items, data, options = {}) {
        const { isRemove = false, isDecrease = false } = options

        let loraTrackingItems = JSON.parse(Cookies.get(TRACKING_COOKIE_KEY.SV_ITEMS) || '[]')

        items.forEach((item) => {
            const clonedItem = JSON.parse(JSON.stringify(item))
            let itemIndex = loraTrackingItems.findIndex((trackingItem) => item.item_id === trackingItem.item_id)

            if (isRemove) {
                loraTrackingItems.splice(itemIndex, 1)
            } else if (itemIndex !== -1) {
                loraTrackingItems[itemIndex] = {
                    ...loraTrackingItems[itemIndex],
                    ...item,
                    quantity: isDecrease
                        ? loraTrackingItems[itemIndex].quantity - clonedItem.quantity
                        : loraTrackingItems[itemIndex].quantity + clonedItem.quantity,
                }
                loraTrackingItems[itemIndex].details = this.updateStoreItemDetails(
                    loraTrackingItems[itemIndex].details,
                    { ...data, quantity: item.quantity },
                    options
                )
            } else {
                clonedItem.details = this.updateStoreItemDetails(
                    [],
                    { ...data, quantity: clonedItem.quantity },
                    options
                )
                loraTrackingItems.push(clonedItem)
            }
        })
        Cookies.set(TRACKING_COOKIE_KEY.SV_ITEMS, JSON.stringify(loraTrackingItems), { expires: 30 })
    }

    syncWithTrackingItems(items) {
        let loraTrackingItems = JSON.parse(Cookies.get(TRACKING_COOKIE_KEY.SV_ITEMS) || '[]')
        return items.map((item) => {
            const trackingItem = loraTrackingItems.find((trackingItem) => item.item_id === trackingItem.item_id)
            if (!trackingItem) return item

            if (trackingItem.quantity !== item.quantity) {
                const isDecrease = trackingItem.quantity > item.quantity
                let totalQuantityChange = Math.abs(trackingItem.quantity - item.quantity)
                trackingItem.details = trackingItem.details.sort((a, b) => b.quantity - a.quantity)

                while (totalQuantityChange > 0) {
                    if (trackingItem.details.every((detail) => detail.quantity === 0)) break

                    trackingItem.details.forEach((detail) => {
                        if (totalQuantityChange === 0 || detail.quantity === 0) return

                        if (isDecrease) detail.quantity--
                        else detail.quantity++

                        totalQuantityChange--
                    })
                }

                trackingItem.details = trackingItem.details.filter((detail) => detail.quantity > 0)
            }

            return {
                ...trackingItem,
                ...item,
            }
        })
    }

    getOrgIdFromScript() {
        if (!LPS_ANALYTICS_SCRIPT) return ''
        const scriptElements = document.querySelectorAll('script')
        let orgId = ''
        for (const script of scriptElements) {
            if (!script.src.startsWith(LPS_ANALYTICS_SCRIPT)) continue
            orgId = new URL(script.src).searchParams.get('orgid')
            if (orgId) break
        }
        return orgId || ''
    }

    checkFromSource() {
        const params = new URLSearchParams(window.location.search)
        let fromSources = JSON.parse(Cookies.get(TRACKING_COOKIE_KEY.SV_CONTEXT) || '{}')

        // Check video/playlist
        let vid = ''
        let pid = ''
        let orgid = this.orgId

        if (params.has('vid')) {
            vid = params.get('vid')
        }
        if (params.has('pid')) {
            pid = params.get('pid')
        }
        if (!orgid && params.has('orgid')) {
            orgid = params.get('orgid')
        }

        // Check in array
        const vidExists = fromSources.video_id === vid && fromSources.playlist_id === pid
        if (vid && !vidExists) {
            fromSources = {
                ...fromSources,
                vid: vid,
                pid: pid,
            }
            Cookies.set(TRACKING_COOKIE_KEY.SV_CONTEXT, JSON.stringify(fromSources))
        }
        const exists = fromSources.shid === params.get('shid') && fromSources.strid === params.get('strid')
        let shid = ''
        let strid = ''
        if (params.has('shid')) {
            shid = params.get('shid')
        }
        if (params.has('strid')) {
            strid = params.get('strid')
        }
        if (shid && strid && !exists) {
            fromSources = {
                ...fromSources,
                shid: shid,
                strid: strid,
            }
        }
        if (orgid) {
            fromSources.orgid = orgid
        }
        Cookies.set(TRACKING_COOKIE_KEY.SV_CONTEXT, JSON.stringify(fromSources), {
            expires: 30,
        })
    }
}

export { LoraStreamAnalytics }
